import {
  type FirestoreDataConverter,
  type QueryDocumentSnapshot,
  Timestamp,
  type WithFieldValue,
} from 'firebase/firestore';
import { isArray, isObject } from 'lodash-es';

import { mapUnknownToDate } from '@ll-platform/frontend/utils/helpers/date';
import type { $TSFixMe } from '@ll-platform/frontend/utils/types/types';

function mapFirestoreDates(data: unknown) {
  if (isObject(data)) {
    if (isArray(data)) {
      for (let i = 0; i < data.length; i++) {
        data[i] = mapFirestoreDates(data[i]);
      }

      return data;
    }

    // Convert Firestore Timestamp to Date
    if (data instanceof Timestamp) {
      return data.toDate();
    }

    for (const key in data) {
      if (Object.prototype.hasOwnProperty.call(data, key)) {
        // @ts-expect-error we checked the key
        data[key] = mapFirestoreDates(data[key]);
      }
    }

    return data;
  }

  return data;
}

export const dataWithIdConverterFactory = <T>() => ({
  toFirestore: (data: T) => data,
  fromFirestore(snap: QueryDocumentSnapshot<T>) {
    const data = snap.data();

    return {
      ...snap.data(),
      id: snap.id,
      ...(typeof data === 'object' && data !== null && 'createdAt' in data
        ? { createdAt: mapUnknownToDate(data.createdAt as Timestamp) }
        : {}),
    } as T & { id: string };
  },
});

// Maps document id and converts Timestamps to Date
export const defaultFirestoreConverterFactory = <
  T,
  P = Partial<Record<keyof T, unknown>>,
>(converter?: {
  toFirestore?: (data: T) => WithFieldValue<P>;
  fromFirestore?: (data: P & { id: string }) => T;
}) =>
  ({
    toFirestore: (data: T) => {
      if (converter?.toFirestore) {
        return converter.toFirestore(data);
      }

      return data;
    },

    fromFirestore(snap: QueryDocumentSnapshot<T>) {
      const data = snap.data();
      if (!isObject(data)) {
        return data;
      }

      const convertedData = mapFirestoreDates(data) as T;

      const toReturn = { ...convertedData, id: snap.id };

      if (converter?.fromFirestore) {
        return converter.fromFirestore(toReturn as $TSFixMe);
      }

      return toReturn;
    },
  }) as FirestoreDataConverter<T>;
